import { Injectable } from "@angular/core";
import {BehaviorSubject, map, Observable} from "rxjs";
import {ActionResult, ADMINISTRATOR_ROLE, OrganiserModule, ORGANISERS_ADMINISTRATOR_ROLE, ORGANIZER_ROLE} from "@model";
import {HttpClient} from "@angular/common/http";
import {EnvironmentService} from "./environment.service";

@Injectable({ providedIn: 'root' })
export class PermissionService {
  private _currentPermissions = new BehaviorSubject<FeaturePermission[] | undefined>(undefined);

  constructor(
      private http: HttpClient,
      private environmentService: EnvironmentService
  ) {}


  get currentPermissions() {
    return this._currentPermissions;
  }

  initPermissions(role?: string): void {
    switch (role) {
      case ORGANIZER_ROLE:
        this._initOrganiserPermission();
        break;
      case ADMINISTRATOR_ROLE:
        this._initAdminPermissions();
        break;
      case ORGANISERS_ADMINISTRATOR_ROLE:
        this._initOrganisersAdminPermissions();
        break;
      default:
        this._initEmptyPermissions();
    }
  }

  private _initOrganisersAdminPermissions() {
    this._setPermissions([
      Features.OrganisersList, Features.CategoriesTab, Features.DiscountsTab, Features.OrganisersEdit,
      Features.LeaguesList, Features.LeagueDetails, Features.RequestsList
    ]);
  }


  private _initAdminPermissions() {
    this._setPermissions([
      Features.OrganisersList, Features.CategoriesTab, Features.DiscountsTab,
      Features.OrganisersEdit, Features.OrganisersAdd,
      Features.Administrators, Features.AccountStatements, Features.AccountStatementsDetails,
      Features.LeaguesList, Features.LeagueDetails, Features.RequestsList, Features.Users
    ]);
  }

  private _initOrganiserPermission(): void {
    const baseFeatures = [
      Features.OrganiserProfile,
      Features.Categories,
      Features.RequestsList,
      Features.DiscountsList,
    ];
    this._fetchAndApplyOrganiserModules(baseFeatures);
  }


  private _fetchAndApplyOrganiserModules(baseFeatures: Features[]): void {
    this.getLoggedInOrganiserActiveModules().subscribe({
      next: (modules: OrganiserModule[]) => {
        const moduleFeatures = this._mapModulesToFeatures(modules);
        this._setPermissions([...baseFeatures, ...moduleFeatures]);
      },
      error: () => {
        this._setPermissions(baseFeatures);
      }
    });
  }

  private _mapModulesToFeatures(modules: OrganiserModule[]): Features[] {
    const features: Features[] = [];
    if (modules.includes(OrganiserModule.LEAGUE_MANAGEMENT)) {
      features.push(Features.LeaguesList, Features.LeagueDetails);
    }
    return features;
  }

  private _initEmptyPermissions() {
    this.currentPermissions.next(undefined);
  }

  emptyPermissions() {
    this.currentPermissions.next(undefined);
  }

  private getLoggedInOrganiserActiveModules(): Observable<OrganiserModule[]> {
    return this.http.get<ActionResult<OrganiserModule[]>>(this.environmentService.getApiUrl() + "/api/organiser/modules").pipe(
        map(value => value?.result?.length ? value.result : [])
    );
  }

  checkPermission(feature: Features, permission: Permission): boolean {
    const featurePermission = this.currentPermissions.getValue()?.find(f => f.feature === feature);
    if (featurePermission) {
      return permission === Permission.View ? featurePermission.permission !== Permission.None : featurePermission.permission === Permission.Edit;
    }
    return false;
  }

  private _setPermissions(features: Features[]) {
    const featurePermissions = features.map(feature => new FeaturePermission(feature, Permission.Edit));
    this.currentPermissions.next(featurePermissions);
  }
}

export enum Features {
  OrganisersList = 'OrganisersList',
  MeasurementsList = 'MeasurementsList',
  OrganiserProfile = 'OrganiserProfile',
  DiscountsList = 'DiscountsList',
  Categories = 'Categories',
  CategoriesTab = 'CategoriesTab',
  DiscountsTab = 'DiscountsTab',
  OrganisersEdit = 'OrganisersEdit',
  OrganisersAdd = 'OrganisersAdd',
  Administrators = 'Administrators',
  AccountStatements = 'AccountStatements',
  AccountStatementsDetails = 'AccountStatementsDetails',
  LeaguesList = 'LeaguesList',
  LeagueDetails = 'LeagueDetails',
  RequestsList = 'RequestsList',
  Users = 'Users',
}

export enum Permission {
  None = 'None',
  View = 'View',
  Edit = 'Edit',
}

export class FeaturePermission {
  constructor(public feature: Features, public permission: Permission) {}
}
